import { createRoot } from "react-dom/client";
import App from "./app/App.tsx";
import "./styles/index.css";

/** Stop the browser from restoring a previous scroll position on load/navigation (SPA). */
if (typeof window !== "undefined" && "scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}

/** Scroll reveal — staggered left→right up animation for grid cards & heading blocks. */
if (typeof window !== "undefined") {
  // Selectors for CONTAINERS whose children we stagger
  const GRID_SEL = [
    "section > div > div.grid",
    "section > div > div.mt-20.grid",
    "section > div > div.grid.grid-cols-1",
  ].join(", ");

  // Selectors for standalone blocks (heading + subtext) that animate as one unit
  const BLOCK_SEL = [
    "section > div > div.text-center.mb-16",
    "section > div > div.text-center.mb-12",
  ].join(", ");

  const STAGGER = 120; // ms between each card

  function revealChildren(parent: HTMLElement) {
    Array.from(parent.children).forEach((child, i) => {
      const el = child as HTMLElement;
      if (el.hasAttribute("data-sr")) return;
      el.style.setProperty("--sr-d", `${i * STAGGER}ms`);
      el.setAttribute("data-sr", "");
    });
  }

  function revealBlock(el: HTMLElement) {
    if (el.hasAttribute("data-sr")) return;
    el.setAttribute("data-sr", "");
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const el = e.target as HTMLElement;
        if (el.hasAttribute("data-sr-grid")) revealChildren(el);
        else revealBlock(el);
        io.unobserve(el);
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
  );

  const register = () => {
    // Grids — mark children as waiting, observe the grid
    document.querySelectorAll<HTMLElement>(GRID_SEL).forEach((grid) => {
      if (grid.hasAttribute("data-sr-grid")) return;
      grid.setAttribute("data-sr-grid", "");
      Array.from(grid.children).forEach((c) => {
        (c as HTMLElement).setAttribute("data-sr-wait", "");
      });
      io.observe(grid);
    });

    // Standalone heading blocks
    document.querySelectorAll<HTMLElement>(BLOCK_SEL).forEach((el) => {
      if (el.hasAttribute("data-sr-wait") || el.hasAttribute("data-sr")) return;
      el.setAttribute("data-sr-wait", "");
      io.observe(el);
    });
  };

  new MutationObserver(register).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

createRoot(document.getElementById("root")!).render(<App />);
  